import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Check, Pencil, AlertTriangle, Info } from 'lucide-react';
import { useConfirm } from '../contexts/ConfirmContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };

const pctColor = (p) => (p == null ? 'var(--text-muted)' : p >= 80 ? '#22c55e' : p >= 50 ? '#C9A84C' : '#ef4444');

const daysLabel = (n) => (n === 1 ? '1 dzień' : `${n} dni`);

const doneLabel = (a) => {
  const parts = [];
  if (a.done_solo) parts.push(`${a.done_solo} samodzielnie`);
  if (a.done_with_trainer) parts.push(`${a.done_with_trainer} z trenerem`);
  return `${a.done} z ${a.planned} treningów${parts.length ? ` (${parts.join(', ')})` : ''}`;
};

// Client card: plan adherence over the last 14 days + the "time to review"
// reminder with "Plan bez zmian" (restarts the 14-day count) or edit.
export const PlanReviewPanel = ({ clientId, navigate }) => {
  const confirm = useConfirm();
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/plan-review/client/${clientId}`, opts)
      .then((r) => alive && setData(r.data)).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [clientId]);

  if (!data) return null;
  const a = data.adherence;

  const keep = async () => {
    if (!(await confirm('Licznik 14 dni zacznie się od dziś, plan zostaje bez zmian.', { title: 'Plan bez zmian?', confirmLabel: 'Zostaw plan' }))) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/plan-review/${data.plan_id}/keep`, {}, opts);
      setData({ ...data, ...r.data });
      toast.success('Zapisano przegląd: plan bez zmian');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się zapisać przeglądu');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="glass rounded-[28px] p-5 sm:p-7 flex flex-col gap-4" data-testid="plan-review-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <span className="big-num text-4xl sm:text-5xl tabular-nums shrink-0" style={{ color: pctColor(a.percent) }} data-testid="adherence-percent">
            {a.percent == null ? '—' : `${a.percent}%`}
          </span>
          <div className="min-w-0">
            <p className="label-text text-[var(--text-muted)]">Realizacja planu · {a.window_days < 14 ? `${daysLabel(a.window_days)} (nowy plan)` : '14 dni'}</p>
            <p className="text-sm mt-1">{doneLabel(a)}</p>
            <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate">{data.plan_name} · {data.frequency} dni/tydzień</p>
          </div>
        </div>
        {!data.review_due && (
          <span className="chip text-[var(--text-muted)]" data-testid="next-review">
            Przegląd za {daysLabel(14 - data.days_since_review)}
          </span>
        )}
      </div>
      {data.cycle_review && <CycleProposals review={data.cycle_review} />}
      {data.review_due && (
        <div className="glass-soft rounded-2xl p-4 border border-[#C9A84C]/40 flex flex-col gap-3" data-testid="review-due">
          <p className="text-sm flex items-start gap-2">
            <CalendarClock className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
            <span><b>Czas na przegląd planu</b> · {daysLabel(data.days_since_review)} bez zmian. Zostaw plan albo daj progresję.</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={keep} disabled={busy}
              className="cta-ghost h-10 px-4 rounded-full text-xs font-semibold flex items-center gap-1.5" data-testid="keep-plan-button">
              <Check className="w-3.5 h-3.5" /> {busy ? 'Zapisywanie...' : 'Plan bez zmian'}
            </button>
            <button type="button" onClick={() => navigate(`/client/${clientId}/plan-builder/${data.plan_id}`)}
              className="cta-primary h-10 px-4 rounded-full text-xs font-bold flex items-center gap-1.5" data-testid="progress-plan-button">
              <Pencil className="w-3.5 h-3.5" /> Edytuj plan
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

// Plans with a cycle (generator): week of the cycle and the 2-week analysis
// proposals - the trainer decides what to apply.
const CycleProposals = ({ review }) => (
  <div className="glass-soft rounded-2xl p-4 flex flex-col gap-2" data-testid="cycle-review">
    <p className="label-text text-[var(--text-muted)]">Cykl · tydzień {review.cycle_week} z {review.weeks_total}</p>
    {review.proposals.map((p, i) => (
      <p key={i} className="text-sm flex items-start gap-2">
        {p.level === 'attention'
          ? <AlertTriangle className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
          : <Info className="w-4 h-4 text-[var(--text-muted)] shrink-0 mt-0.5" />}
        <span>{p.text}</span>
      </p>
    ))}
  </div>
);

// Dashboard: clients whose plan is due for a review (hidden when none).
export const PlanReviewCard = ({ items, onOpen }) => {
  if (!items.length) return null;
  return (
    <section className="glass rounded-[28px] p-6 flex flex-col gap-3" data-testid="plan-review-due">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-extrabold tracking-tight">Przegląd planu</h2>
        <span className="chip border-[#C9A84C]/50">{items.length}</span>
      </div>
      <p className="text-xs text-[var(--text-muted)]">Plany bez zmian od 14+ dni. Realizacja z ostatnich 14 dni.</p>
      {items.map((it) => (
        <button key={it.client_id} type="button" onClick={() => onOpen(it.client_id)}
          className="glass-soft rounded-2xl px-4 py-3 flex items-center justify-between gap-3 text-left">
          <span className="min-w-0">
            <span className="block text-sm font-bold truncate">{it.client_name}</span>
            <span className="block text-xs text-[var(--text-muted)] truncate">
              {it.adherence.done}/{it.adherence.planned} treningów · {daysLabel(it.days_since_review)} bez zmian
            </span>
          </span>
          <span className="big-num text-xl tabular-nums shrink-0" style={{ color: pctColor(it.adherence.percent) }}>
            {it.adherence.percent == null ? '—' : `${it.adherence.percent}%`}
          </span>
        </button>
      ))}
    </section>
  );
};
